"use client";

import { c as _c } from "react/compiler-runtime";
import { ReferralWorkflowTimeline, type ReferralWorkflowStatus, type ReferralWorkflowStep } from "@/configuration/groups/usrc/components/referral-timeline";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { ensurePatientOutputSchema } from "@/src/models/ensure-patient-output";
import { TaskStatus, type Task } from "@/src/models/task";
import { TaskStepStatus, type TaskStep } from "@/src/models/task-step";

// Step outputs are read field by field rather than through the producers' zod schemas: those live
// beside the EHR integrations, which pull server-only modules into the client bundle.
function outputString(step: TaskStep | undefined, key: string): string | null {
  const value = step?.output?.[key];
  return typeof value === "string" && value.trim() ? value : null;
}
function outputNumber(step: TaskStep | undefined, key: string): number | null {
  const value = step?.output?.[key];
  return typeof value === "number" ? value : null;
}
function stepStatus(step: TaskStep | undefined, confirmed: boolean, options: {
  needsAction?: boolean;
  skipped?: boolean;
} = {}): ReferralWorkflowStatus {
  if (!step) return "not_run";
  if (step.status === TaskStepStatus.FAILED) return "failed";
  if (options.needsAction) return "needs_action";
  if (step.status === TaskStepStatus.ASYNC || step.status === TaskStepStatus.PAUSED) return "waiting";
  // Early exits are persisted as completed too, so require success evidence before the checkmark.
  if (step.status === TaskStepStatus.COMPLETED && confirmed) return options.skipped ? "not_needed" : "automated";
  return "needs_action";
}
export function UsrcReferralSteps(t0) {
  const $ = _c(33);
  const {
    task,
    taskSteps
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== taskSteps) {
    const orderedTaskSteps = taskSteps.toSorted(_temp);
    const findStep = key => orderedTaskSteps.find(step => step.stepKey === key);
    const lookupStep = findStep(StepKey.USRC_REGISTRATION_LOOKUP);
    const patientStep = findStep(StepKey.ENSURE_PATIENT);
    const questionnaireStep = findStep(StepKey.USRC_REGISTRATION_QUESTIONNAIRE);
    const uploadStep = findStep(StepKey.EHR_DOC_UPLOAD);
    const noteStep = findStep(StepKey.USRC_REGISTRATION_NOTE);
    const conversationStep = findStep(StepKey.USRC_REGISTRATION_CONVERSATION);
    const disposition = outputString(lookupStep, "disposition");
    const matchedRegistrationId = outputNumber(lookupStep, "registrationId");
    const registration = ensurePatientOutputSchema.safeParse(patientStep?.output);
    const unmappedClassification = outputString(uploadStep, "outcome") === "unmapped_document_type" ? outputString(uploadStep, "classification") : null;
    const unmapped = outputString(uploadStep, "outcome") === "unmapped_document_type";
    const discharged = disposition === "involuntarily_discharged";
    const questionnaireOutcome = outputString(questionnaireStep, "outcome");
    const noQuestionnaire = questionnaireOutcome === "not_applicable";
    const questionnaireUnpostable = questionnaireOutcome === "unpostable";
    const uploaded = outputString(uploadStep, "fileId") !== null;
    t3 = {
      title: "Find the patient's registration",
      status: stepStatus(lookupStep, disposition !== null, {
        needsAction: discharged
      }),
      summary: discharged ? "This patient was involuntarily discharged. The referral stops here." : "Searched Intake Manager for an existing registration for this patient.",
      content: disposition ? <p className="text-sm">{disposition === "update" ? `Matched existing registration ${matchedRegistrationId ?? "(id not recorded)"}.` : disposition === "create" ? "No existing registration; a new one was created in the next step." : "Intake Manager reports this patient as involuntarily discharged. Do not register them again."}</p> : undefined
    };
    t4 = {
      title: "Create or update the registration",
      status: stepStatus(patientStep, registration.success),
      summary: "Registered the patient in Intake Manager, or updated the registration the lookup found.",
      content: registration.success ? <p className="text-sm">{registration.data.created ? "New registration" : "Existing registration"}{" "}<strong>{registration.data.patientEhrId}</strong>. Use this ID to find the chart in Intake Manager.</p> : undefined
    };
    let t8;
    if ($[8] !== questionnaireUnpostable) {
      t8 = questionnaireUnpostable ? <p className="text-sm">USRC has not published the questionnaire question and answer IDs, so the extracted answers cannot be posted yet. Enter them in Intake Manager by hand.</p> : undefined;
      $[8] = questionnaireUnpostable;
      $[9] = t8;
    } else {
      t8 = $[9];
    }
    t5 = {
      title: "Answer the dialysis questionnaire",
      status: stepStatus(questionnaireStep, true, {
        needsAction: questionnaireUnpostable,
        skipped: noQuestionnaire
      }),
      summary: noQuestionnaire ? "This packet did not include a dialysis questionnaire." : questionnaireUnpostable ? "No answers were posted. Enter them in Intake Manager by hand." : "Posted the isolation, equipment and mobility answers to the registration.",
      content: t8
    };
    t6 = {
      title: "Upload the referral packet",
      status: stepStatus(uploadStep, uploaded, {
        needsAction: unmapped
      }),
      summary: unmapped ? "The referral packet was not uploaded." : "Indexed the referral PDF to the registration in Intake Manager.",
      content: unmapped ? <p className="text-sm">No Intake Manager document type is configured for{" "}<strong>{unmappedClassification ?? "this classification"}</strong>, so the document was not uploaded. File it by hand and ask for the document type ID to be added.</p> : undefined
    };
    let t9;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <p className="text-sm">Intake Manager returns no confirmation for this write. Check the note exists.</p>;
      $[10] = t9;
    } else {
      t9 = $[10];
    }
    t7 = {
      title: "Add the handoff note",
      status: stepStatus(noteStep, true),
      summary: "Recorded the reason for referral, diagnoses and intake note on the registration.",
      content: t9
    };
    t1 = "Send the handoff message";
    t2 = stepStatus(conversationStep, true);
    $[0] = taskSteps;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
    t7 = $[7];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <p className="text-sm">Intake Manager returns no confirmation for this write. Check the message exists.</p>;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t1 || $[13] !== t2) {
    t9 = {
      title: t1,
      status: t2,
      summary: "Posted the same summary to the registration's conversation thread.",
      content: t8
    };
    $[12] = t1;
    $[13] = t2;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let isCompleted;
  let isProcessing;
  let steps;
  if ($[15] !== t3 || $[16] !== t4 || $[17] !== t5 || $[18] !== t6 || $[19] !== t7 || $[20] !== t9 || $[21] !== task.status) {
    steps = [t3, t4, t5, t6, t7, t9];
    isCompleted = task.status === TaskStatus.COMPLETED;
    isProcessing = task.status === TaskStatus.PROCESSING;
    const hasFailedStep = steps.some(_temp2);
    const needsAttention = steps.some(_temp3);
    const hasUnfinishedStep = steps.some(_temp4);
    const t10 = hasFailedStep ? "failed" : needsAttention ? "needs_action" : hasUnfinishedStep && !isCompleted ? "waiting" : "verify";
    const t11 = isCompleted ? "Automation finished and completed this task on its own. Nobody has checked Intake Manager yet." : hasFailedStep || needsAttention ? "Resolve the outstanding steps above before verifying this task." : hasUnfinishedStep ? "The workflow is still running. Verify the results when it finishes." : "Confirm the registration, questionnaire, referral document and handoff note in Intake Manager.";
    let t12;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <p className="text-sm">Open the registration in Intake Manager and confirm the referral landed. The handoff note and message return no confirmation, so check those by hand.</p>;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    let t13;
    if ($[26] !== t10 || $[27] !== t11) {
      t13 = {
        title: "Verify in Intake Manager",
        status: t10,
        summary: t11,
        content: t12
      };
      $[26] = t10;
      $[27] = t11;
      $[28] = t13;
    } else {
      t13 = $[28];
    }
    steps.push(t13);
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t9;
    $[21] = task.status;
    $[22] = isCompleted;
    $[23] = isProcessing;
    $[24] = steps;
  } else {
    isCompleted = $[22];
    isProcessing = $[23];
    steps = $[24];
  }
  const t10 = !isCompleted && taskSteps.length === 0 ? "No Intake Manager sync steps have been recorded for this task yet." : undefined;
  let t11;
  if ($[29] !== isProcessing || $[30] !== steps || $[31] !== t10) {
    t11 = <ReferralWorkflowTimeline steps={steps} isProcessing={isProcessing} waitingMessage={t10} />;
    $[29] = isProcessing;
    $[30] = steps;
    $[31] = t10;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  return t11;
}
function _temp4(step_2) {
  return step_2.status === "not_run" || step_2.status === "waiting";
}
function _temp3(step_1) {
  return step_1.status === "needs_action" || step_1.status === "failed";
}
function _temp2(step_0) {
  return step_0.status === "failed";
}
function _temp(a, b) {
  return a.stepIndex - b.stepIndex;
}